import { useLocation, useNavigate } from 'react-router-dom';

// @mui
import { useTheme } from '@mui/material/styles';
import { Stack, AppBar, Toolbar, Badge } from '@mui/material';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// config
import { HEADER, NAV } from '../../../config-global';
// components
import Logo from '../../../components/logo';
import SplitButton from '../../../components/SplitButtons';
import MenuButton from '../../../components/MenuButton';
import { useSettingsContext } from '../../../components/settings';
//
import AccountPopover from './AccountPopover';
// import NotificationsPopover from './NotificationsPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { PATH_DASHBOARD, SCROLLERS_PATHS } from '../../../routes/paths';
import { OzonPostingsSyncToolbar } from '../../../pages/dashboard/ozon/postings/constants/OzonPostingsSyncToolbar';
import { WbPostingsSyncToolbar } from '../../../pages/dashboard/wb/postings/constants/WbPostingsSyncToolbar';
import { WbCatalogProductsSyncToolbar } from '../../../pages/dashboard/wb/catalog-products/constants/WbCatalogProductsSyncToolbar';
import { AvitoPostingsSyncToolbar } from '../../../pages/dashboard/avito/postings/constants/AvitoPostingsSyncToolbar';
import { headerOptions } from '../../../routes/header-options';
import { useRedux } from '../../../hooks/useRedux';
import { useI18n } from 'iomart-hooks';

// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
  onOpenSearchbar: VoidFunction;
  onCloseSearchBar: VoidFunction;
  isSearchbarOpen: boolean;
};

export default function Header({ onOpenNav, onOpenSearchbar, onCloseSearchBar, isSearchbarOpen }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasSearch, hasFilter, hasFilterFields, setFilterOpen } = useRedux();
  const { t } = useI18n(false);

  // Определяем ближайший (самый глубокий) scroller-путь с учётом динамических сегментов
  const scrollerPath = SCROLLERS_PATHS
    .filter((item) => {
      // Заменяем :param или 0/undefined на маску «segment»
      const pattern = '^' + item
        .replace(/:[^/]+/g, '[^/]+')
        .replace(/\b0\b/g, '[^/]+')
        .replace(/undefined/g, '[^/]+') + '(?:/|$)';
      return new RegExp(pattern).test(location.pathname);
    })
    .sort((a, b) => b.length - a.length)[0];

  // Подставляем реальные значения вместо :params/0/undefined
  const resolvedScrollerPath = scrollerPath
    ? scrollerPath
        .split('/')
        .map((part, idx) =>
          part.startsWith(':') || part === '0' || part === 'undefined'
            ? location.pathname.split('/')[idx]
            : part
        )
        .join('/')
    : undefined;

  // Функция для поиска headerParams с поддержкой переменных :id
  const findHeaderParams = () => {
    const currentParts = location.pathname.split('/');
    for (const key of Object.keys(headerOptions)) {
      const keyParts = key.split('/');
      if (keyParts.length !== currentParts.length) continue;
      let match = true;
      for (let i = 0; i < keyParts.length; i++) {
        // Обрабатываем динамические сегменты пути: ':param', значение '0' или 'undefined' (шаблон)
        if (keyParts[i].startsWith(':') || keyParts[i] === '0' || keyParts[i] === 'undefined') continue;
        if (keyParts[i] !== currentParts[i]) {
          match = false;
          break;
        }
      }
      if (match) return headerOptions[key];
    }
    return undefined;
  };

  const headerParams = findHeaderParams();

  const ozonPostingsBase = PATH_DASHBOARD.ozon.postings.scroller;
  const showOzonPostingsSync =
    location.pathname === ozonPostingsBase || location.pathname.startsWith(`${ozonPostingsBase}/`);
  const wbPostingsBase = PATH_DASHBOARD.wb.postings.scroller;
  const showWbPostingsSync =
    location.pathname === wbPostingsBase || location.pathname.startsWith(`${wbPostingsBase}/`);
  const wbCatalogBase = PATH_DASHBOARD.wb.catalogProducts.scroller;
  const showWbCatalogSync =
    location.pathname === wbCatalogBase || location.pathname.startsWith(`${wbCatalogBase}/`);
  const avitoPostingsBase = PATH_DASHBOARD.avito.postings.scroller;
  const showAvitoPostingsSync =
    location.pathname === avitoPostingsBase || location.pathname.startsWith(`${avitoPostingsBase}/`);

  const options = headerParams?.buttons?.map((button) => {
    const locationPathSplitted = location.pathname.split('/');
    const path = button.path
      .split('/')
      .map((part, index) => part.startsWith(':') ? locationPathSplitted[index] : (part === '0' ? locationPathSplitted[index] : part))
      .join('/');

    return {
      ...button,
      path,
    };
  });

  const isScroller = location.pathname === scrollerPath;

  const { themeLayout } = useSettingsContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');
  const isDesktopMD = useResponsive('up', 'md');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const onFilterOpen = () => { setFilterOpen(true) }

  const renderContent = (
    <>
      {isDesktop && isNavHorizontal && <Logo sx={{ mr: 2.5 }} />}

      {!isDesktop && (
        <IconButtonAnimate size="small" onClick={onOpenNav} sx={{ color: 'text.primary' }}>
          <IconEvaMenu2Fill width={20} height={20} />
        </IconButtonAnimate>
      )}

      {!isDesktopMD && !isDesktop && (
        <>
          <IconButtonAnimate
            onClick={() => {
              navigate(-1);
            }}
            size="small"
          >
            <IconIcBaselineSubdirectoryArrowLeft width={20} height={20} />
          </IconButtonAnimate>
          <IconButtonAnimate
            onClick={() => {
              navigate(PATH_DASHBOARD.general.app);
            }}
            size="small"
          >
            <IconEvaHomeFill width={20} height={20} />
          </IconButtonAnimate>
          {resolvedScrollerPath && (
            <IconButtonAnimate
              onClick={() => {
                if (location.pathname !== resolvedScrollerPath) {
                  navigate(resolvedScrollerPath);
                }
              }}
              size="small"
            >
              <IconMaterialSymbolsListAltOutline width={20} height={20} />
            </IconButtonAnimate>
          )}
          {isScroller && (
            <>
              <IconButtonAnimate size="small" onClick={onOpenSearchbar}>
                <Badge badgeContent={hasSearch ? 1 : 0} variant="dot" color="error">
                  <IconEvaSearchFill width={20} height={20} />
                </Badge>
              </IconButtonAnimate>
              {hasFilterFields && !headerParams?.filterOff && (
                <IconButtonAnimate onClick={onFilterOpen} size="small">
                  <Badge badgeContent={hasFilter ? 1 : 0} variant="dot" color="error">
                    <IconMaterialSymbolsFilterList width={20} height={20} />
                  </Badge>
                </IconButtonAnimate>
              )}
            </>
          )}
        </>
      )}

      {(isDesktop || isDesktopMD) && (
        <>
          <MenuButton
            onClick={() => {
              navigate(-1);
            }}
            title={t('Back')}
            icon={<IconIcBaselineSubdirectoryArrowLeft width={20} height={20} />}
          />
          <MenuButton
            onClick={() => {
              navigate(PATH_DASHBOARD.general.app);
            }}
            title={t('Home')}
            icon={<IconEvaHomeFill width={20} height={20} />}
          />
          {resolvedScrollerPath && (
            <MenuButton
              onClick={() => {
                if (!isScroller) {
                  navigate(resolvedScrollerPath);
                }
              }}
              title={t('Scroller')}
              icon={<IconMaterialSymbolsListAltOutline width={20} height={20} />}
            />
          )}
          {isScroller && (
            <>
              <MenuButton
                onClick={onOpenSearchbar}
                title={t('Search')}
                icon={<IconEvaSearchFill width={20} height={20} />}
                hasData={hasSearch}
              />
              {hasFilterFields && !headerParams?.filterOff && (
                <MenuButton
                  onClick={onFilterOpen}
                  title={t('Filters')}
                  icon={<IconMaterialSymbolsFilterList width={20} height={20} />}
                  hasData={hasFilter}
                />
              )}
            </>
          )}
        </>
      )}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
      >
        {showOzonPostingsSync && <OzonPostingsSyncToolbar />}
        {showWbPostingsSync && <WbPostingsSyncToolbar />}
        {showAvitoPostingsSync && <AvitoPostingsSyncToolbar />}
        {showWbCatalogSync && <WbCatalogProductsSyncToolbar />}
        {options && <SplitButton options={options} dense />}

        {/* <NotificationsPopover /> */}

        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <AppBar
      sx={{
        boxShadow: 'none',
        height: HEADER.H_MOBILE,
        zIndex: theme.zIndex.appBar - 1,
        ...bgBlur({
          color: theme.palette.background.default,
        }),
        transition: theme.transitions.create(['height'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(isDesktop && {
          width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
          height: HEADER.H_DASHBOARD_DESKTOP,
          ...(isOffset && {
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
          }),
          ...(isNavHorizontal && {
            width: 1,
            bgcolor: 'background.default',
            height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            borderBottom: `dashed 1px ${theme.palette.divider}`,
          }),
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
          }),
        }),
      }}
    >
      <Toolbar
        sx={{
          height: 1,
          px: { lg: 5 },
        }}
      >
        {renderContent}
      </Toolbar>
    </AppBar>
  );
}
